<template>
  <!-- Filters and Actions -->
  <div :class="$style.iapHeader">
    <div :class="$style.filters">
      <!-- Platform Filter -->
      <div :class="$style.filterItem">
        <label>Search</label>
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText v-model="filters['global'].value" placeholder="Type something" />
        </IconField>
      </div>
      <div :class="$style.filterItem">
        <label>Platform</label>
        <ToggleButtonGroup v-model="selectedPlatform" :options="platformOptions" @refetch="fetchIAPList" />
      </div>
    </div>
    <div v-if="pricingTemplatesEnabled" :class="$style.tableActions">
      <Button
        v-if="selectedPlatform === 'ANDROID'"
        label="New Google Play IAP"
        icon="pi pi-plus"
        @click="openCreateGooglePlayIAPModal()"
      />
    </div>
  </div>

  <!-- Loading and Empty State -->
  <div v-if="loadingIAPs">Loading...</div>
  <div v-else-if="!loadingIAPs && IAPs && IAPs.length === 0" :class="$style.emptyState">
    <VdIcon store="fa" name="box-open" :class="$style.emptyStateIcon" custom-size="100px" color="#8ec7ff" />
    <p :class="$style.emptyStateText">No in-app purchases found for the selected country.</p>
  </div>

  <!-- IAP List -->
  <DataTable
    v-if="!loadingIAPs && IAPs && IAPs.length > 0"
    v-model:filters="filters"
    :value="IAPs"
    :global-filter-fields="['name', 'productId']"
    data-key="id"
    table-style="min-width: 60rem; overflow: visible;"
    :row-class="disableRow"
  >
    <Column field="name" header="Reference">
      <template #body="slotProps">
        <div :class="$style.iapStatus">
          <VdTooltip
            v-if="areAnyStoreItemsPublished(slotProps.data.storeItems)"
            orientation="top"
            :content="tooltipContent"
          >
            <VdIcon store="fa" name="lock" />
          </VdTooltip>
          <span>{{ slotProps.data.name }}</span>
        </div>
      </template>
    </Column>
    <Column field="productId" header="Product ID"></Column>
    <Column field="type" header="Type">
      <template #body="slotProps">
        {{ mapIAPTypeToReadableString(slotProps.data.type) }}
      </template>
    </Column>
    <Column header="Price" style="width: 200px; text-align: right" header-style="text-align: right">
      <template #body="{ data: iap }">
        <div>
          <b>{{ formatPrice(iap.pricing[0].value, iap.pricing[0].currency) }}</b>
        </div>
        <router-link
          v-if="pricingTemplatesEnabled && iap.pricingTemplateLink.length > 0"
          :to="`/games/${gameId}/${versionId}/iap/pricingTemplates/${iap.pricingTemplateLink[0].pricingTemplate?.id}`"
          target="_blank"
        >
          <Tag
            v-tooltip.top="mapTooltipFromLinkStatus(iap.pricingTemplateLink[0])"
            :value="iap.pricingTemplateLink[0].pricingTemplate?.name"
            :icon="mapIconFromLinkStatus(iap.pricingTemplateLink[0])"
            :severity="mapSeverityFromLinkStatus(iap.pricingTemplateLink[0])"
          />
        </router-link>
      </template>
    </Column>
    <Column>
      <template #header>
        <div>
          Status
          <VdTooltip orientation="top" :content="tooltipContent">
            <VdIcon store="fas" name="circle-info" color="var(--vd-color-blue-default)" />
          </VdTooltip>
        </div>
      </template>
      <template #body="slotProps">
        <div v-if="areAnyStoreItemsPublished(slotProps.data.storeItems)">Published</div>
        <div v-else>Not published</div>
      </template>
    </Column>
    <Column header="Non-Consumables">
      <template #body="slotProps">
        <StoreItemsManager
          :model-value="slotProps.data.storeItems"
          :game-id="gameId"
          :version-id="versionId"
          :max-visible-tags="4"
          :disabled="areAnyStoreItemsPublished(slotProps.data.storeItems)"
          :iap-id="slotProps.data.id"
        />
      </template>
    </Column>
    <Column v-if="pricingTemplatesEnabled" :class="$style.actions">
      <template #body="{ data }">
        <Button
          v-if="data.platform === 'ANDROID'"
          icon="pi pi-ellipsis-v"
          plain
          aria-controls="iap-actions-menu"
          @click="onIAPSelected($event, data)"
        ></Button>
      </template>
    </Column>
  </DataTable>
  <Menu id="iap_actions_menu" ref="iapActionsMenu" popup :model="iapActionItems" />
  <CreateIAPDialog v-model:visible="iapModalVisible" :app-id="gameId" @submit="fetchIAPList" />
  <SyncIAPDialog v-model="selectedIAP" v-model:visible="syncModalVisible" :app-id="gameId" @submit="fetchIAPList" />
  <UnlinkIAPDialog v-model="selectedIAP" v-model:visible="unlinkModalVisible" :app-id="gameId" @submit="fetchIAPList" />
  <UpdateIapPriceDialog
    v-model="selectedIAP"
    v-model:visible="iapPriceModalVisible"
    :app-id="gameId"
    @submit="fetchIAPList"
  />
  <Toast position="top-center" />
</template>

<script setup>
import androidIcon from "@/assets/icons/android.svg";
import iosIcon from "@/assets/icons/ios.svg";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { formatPrice } from "@/utils/formatPrice";
import { mapIAPTypeToReadableString } from "@/utils/iapEnumMapping";
import { VdTooltip } from "@voodoo.io/voodoo-ui";
import Column from "primevue/column";
import { Button, DataTable, IconField, InputIcon, InputText, Menu, Tag } from "primevue";
import { FilterMatchMode } from "@primevue/core/api";
import Toast from "primevue/toast";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import useIAPs from "./hooks/useIAPs";
import UpdateIapPriceDialog from "./components/UpdateIapPriceDialog.vue";
import CreateIAPDialog from "./components/CreateIAPDialog.vue";
import { PRICING_TEMPLATE_ALLOWED_APP_IDS } from "@/constants";
import SyncIAPDialog from "@/modules/IAP/components/SyncIAPDialog.vue";
import UnlinkIAPDialog from "@/modules/IAP/components/UnlinkIAPDialog.vue";

const route = useRoute();
const router = useRouter();

const gameId = ref(route.params.gameId);
const versionId = ref(route.params.versionId);

const { getIAPs, IAPs } = useIAPs();

const loadingIAPs = ref(false);
const selectedPlatform = ref("ANDROID");
const selectedIAP = ref();
const iapActionsMenu = ref();
const iapModalVisible = ref(false);
const iapPriceModalVisible = ref(false);
const syncModalVisible = ref(false);
const unlinkModalVisible = ref(false);
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const pricingTemplatesEnabled = computed(() => PRICING_TEMPLATE_ALLOWED_APP_IDS.includes(gameId.value));

const fetchIAPList = async () => {
  loadingIAPs.value = true;
  await getIAPs(gameId.value, versionId.value, selectedPlatform.value);
  loadingIAPs.value = false;
};

const iapActionItems = ref([
  {
    label: "Sync from price template",
    command: async () => {
      syncModalVisible.value = true;
    },
    visible: () => !!selectedIAP.value.pricingTemplateLink[0],
  },
  {
    label: "Unlink from price template",
    command: async () => {
      unlinkModalVisible.value = true;
    },
    visible: () => !!selectedIAP.value.pricingTemplateLink[0],
  },
  {
    label: "Change price",
    command: () => {
      iapPriceModalVisible.value = true;
    },
    visible: () => true,
  },
]);

const onIAPSelected = (event, data) => {
  selectedIAP.value = data;
  iapActionsMenu.value.toggle(event);
};

const tooltipContent =
  "Once published, the IAP will be locked and associations with<br />different non-consumables won’t be possible. <a href='https://www.notion.so/voodoo/Supporting-Non-Consumables-with-Offers-Shop-16fa0b481db480729a96d15ba772945d' target='_blank' rel='noopener noreferrer'>More info</a>";

const platformOptions = ref([
  { label: "Android", value: "ANDROID", icon: androidIcon },
  { label: "iOS", value: "IOS", icon: iosIcon },
]);
const areAnyStoreItemsPublished = (storeItems) => {
  if (storeItems?.length === 0) {
    return false;
  }

  return storeItems.some((item) => item.isPublished);
};

const disableRow = (data) => {
  if (areAnyStoreItemsPublished(data.storeItems)) {
    return "iap-row-disabled";
  }

  return "";
};

const updateRoute = () => {
  const query = { ...route.query };
  if (selectedPlatform.value) {
    query.platform = selectedPlatform.value;
  } else {
    delete query.platform;
  }
  router.push({ query });
};

const now = new Date();
const sevenDaysAgo = new Date(now.setDate(now.getDate() - 7));

const mapIconFromLinkStatus = (link) => {
  const updatedAtDate = new Date(link.updatedAt);
  if (updatedAtDate < sevenDaysAgo) {
    return "pi pi-history";
  }
  return link.status === "SYNCHED" ? "pi pi-verified" : "pi pi-exclamation-circle";
};

const mapSeverityFromLinkStatus = (link) => {
  const updatedAtDate = new Date(link.updatedAt);
  if (updatedAtDate < sevenDaysAgo) {
    return "secondary";
  }
  return link.status === "SYNCHED" ? "success" : "danger";
};

const mapTooltipFromLinkStatus = (link) => {
  const updatedAtDate = new Date(link.updatedAt);
  if (updatedAtDate < sevenDaysAgo) {
    return "This link status hasn't been updated for a while. We can't ensure its state.";
  }
  return link.status === "SYNCHED"
    ? "This link is up to date."
    : "We've detected differences between this IAP and its linked Pricing Template!";
};

watch(selectedPlatform, () => {
  updateRoute();
});

onMounted(async () => {
  const { query } = route;
  selectedPlatform.value = query.platform || "ANDROID";
  await fetchIAPList();
});

function openCreateGooglePlayIAPModal() {
  iapModalVisible.value = true;
}
</script>

<style module lang="scss">
.iapHeader {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.filters {
  display: flex;
  align-items: center;
  gap: 16px;
}

.filterItem {
  display: flex;
  flex-direction: column;
}

.actions {
  text-align: end;

  button {
    background: none;
    border: none;
    color: var(--vd-color-blue-600);
  }
}

.tableActions {
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.toolbarPosition {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

.emptyState {
  padding-top: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.emptyStateText {
  font-size: 1.5rem;
  font-weight: 500;
  margin-top: 1rem;
  color: var(--vd-color-blue-light-4);
}

.iapStatus {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>

<style scoped>
:deep(.p-datatable-table-container) {
  overflow: visible !important;
}

:deep(.iap-row-disabled) {
  opacity: 0.5;
}

:deep(.p-tag) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  gap: 0.25rem;

  .p-tag-icon {
    font-size: 0.75rem;
    width: 0.75rem;
    height: 0.75rem;
  }

  &.p-tag-success {
    background-color: #f4fcf7;
    color: #188a42;
  }

  &.p-tag-secondary {
    background-color: #f1f5f9;
    color: #475569;
  }

  &.p-tag-danger {
    background-color: #fee2e2;
    color: #b91c1c;
  }
}
</style>
